import { customFetch, getStoredToken } from './apiService';
import toast from 'react-hot-toast';
import { getFriendlyErrorMessage, FRIENDLY_ERROR_MAP } from '../utils/aiErrorUtils';



export interface AiHeavyJobPayload {
  prompt: string;
  fileUrls?: string[];
  fileUrl?: string;
  requestedModel?: string;
  subject?: string;
  grade?: string;
  difficulty?: string;
  questionCount?: number;
  targetSetId?: string;
}

export interface AiHeavyJobStored {
  jobId: string;
  timestamp: number;
  params: AiHeavyJobPayload;
}

export interface AiJobStatusEvent {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  message?: string;
  resultText?: string;
  questions?: any[];
  errorMessage?: string;
  errorCode?: string;
}


const STORAGE_KEY = 'ai_heavy_job';
const MAX_JOB_AGE_MS = 3600 * 1000; // 1 hour (thời gian sống của Job trên Backend)

/**
 * Lấy token xác thực từ storage
 */
const getAuthHeaders = (): Record<string, string> => {
  const token = getStoredToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * Bước 1: Khởi tạo Queue
 * Gọi POST /api/v1/ai/generate-heavy
 * Bắt mã 429 -> Hiển thị Toast thông báo rate limit
 */
export const startAiHeavyJob = async (payload: AiHeavyJobPayload): Promise<{ jobId: string; status: string }> => {
  try {
    // Sanitize payload thành các giá trị nguyên thủy an toàn trước khi serialize JSON
    const cleanPayload: AiHeavyJobPayload = {
      prompt: typeof payload?.prompt === 'string' ? payload.prompt : String(payload?.prompt || ''),
      fileUrls: Array.isArray(payload?.fileUrls) ? payload.fileUrls.map(u => (typeof u === 'string' ? u : String(u || ''))).filter(Boolean) : [],
      fileUrl: typeof payload?.fileUrl === 'string' ? payload.fileUrl : undefined,
      requestedModel: typeof payload?.requestedModel === 'string' ? payload.requestedModel : (payload?.requestedModel ? String(payload.requestedModel) : undefined),
      subject: typeof payload?.subject === 'string' ? payload.subject : (payload?.subject ? String(payload.subject) : undefined),
      grade: typeof payload?.grade === 'string' ? payload.grade : (payload?.grade ? String(payload.grade) : undefined),
      difficulty: typeof payload?.difficulty === 'string' ? payload.difficulty : (payload?.difficulty ? String(payload.difficulty) : undefined),
      questionCount: typeof payload?.questionCount === 'number' ? payload.questionCount : (Number(payload?.questionCount) || 5),
      targetSetId: typeof payload?.targetSetId === 'string' ? payload.targetSetId : (payload?.targetSetId ? String(payload.targetSetId) : undefined)
    };

    const res = await customFetch('/api/v1/ai/generate-heavy', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      credentials: 'include',
      body: JSON.stringify(cleanPayload)
    });

    // PHẦN 5: Xử lý Rate Limit - Bắt mã 429
    if (res.status === 429) {
      const rateLimitMsg = "Hệ thống AI đang bận hoặc bạn thao tác quá nhanh. Vui lòng thử lại sau 1 phút.";
      toast.error(rateLimitMsg, { duration: 5000, id: 'ai-rate-limit-toast' });
      const err: any = new Error(rateLimitMsg);
      err.status = 429;
      throw err;
    }

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.jobId) {
      console.error('[AI Heavy Job Start Failed Response]:', res.status, data);
      const rawMsg = data.message || data.error || (res.status === 401 ? 'Phiên đăng nhập không hợp lệ hoặc đã hết hạn' : `Yêu cầu thất bại (${res.status})`);
      const friendly = getFriendlyErrorMessage(data.code || rawMsg);
      const err: any = new Error(friendly);
      err.status = res.status;
      err.code = data.code;
      throw err;
    }

    // Bước 5: Ngay khi nhận được jobId ở Bước 1, Frontend lưu chuỗi jobId này kèm timestamp vào localStorage
    saveActiveJob(data.jobId, payload);

    return {
      jobId: data.jobId,
      status: data.status || 'pending'
    };
  } catch (err: any) {
    if (err.status === 429) throw err;
    console.error('[startAiHeavyJob Error]:', err);
    const msg = getFriendlyErrorMessage(err);
    const normalizedErr: any = new Error(msg);
    normalizedErr.status = err?.status;
    normalizedErr.code = err?.code;
    throw normalizedErr;
  }
};

/**
 * Bước 5: Lưu active job vào localStorage kèm timestamp
 */
export const saveActiveJob = (jobId: string, params: AiHeavyJobPayload): void => {
  try {
    const cleanParams: AiHeavyJobPayload = {
      prompt: typeof params?.prompt === 'string' ? params.prompt : String(params?.prompt || ''),
      fileUrls: Array.isArray(params?.fileUrls) ? params.fileUrls.map(u => String(u || '')).filter(Boolean) : [],
      fileUrl: typeof params?.fileUrl === 'string' ? params.fileUrl : undefined,
      requestedModel: typeof params?.requestedModel === 'string' ? params.requestedModel : (params?.requestedModel ? String(params.requestedModel) : undefined),
      subject: typeof params?.subject === 'string' ? params.subject : (params?.subject ? String(params.subject) : undefined),
      grade: typeof params?.grade === 'string' ? params.grade : (params?.grade ? String(params.grade) : undefined),
      difficulty: typeof params?.difficulty === 'string' ? params.difficulty : (params?.difficulty ? String(params.difficulty) : undefined),
      questionCount: typeof params?.questionCount === 'number' ? params.questionCount : (Number(params?.questionCount) || 5),
      targetSetId: typeof params?.targetSetId === 'string' ? params.targetSetId : (params?.targetSetId ? String(params.targetSetId) : undefined)
    };

    const record: AiHeavyJobStored = {
      jobId: String(jobId),
      timestamp: Date.now(),
      params: cleanParams
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch (e) {
    console.warn('[STORAGE] Could not save active AI job', e);
  }
};

/**
 * Bước 5: Kiểm tra và lấy jobId từ localStorage khi Component Mount / Page Load
 * Chỉ trả về nếu chưa quá 1 giờ
 */
export const getActiveJob = (): AiHeavyJobStored | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: AiHeavyJobStored = JSON.parse(raw);
    if (!parsed || !parsed.jobId || !parsed.timestamp) {
      clearActiveJob();
      return null;
    }

    const age = Date.now() - parsed.timestamp;
    if (age > MAX_JOB_AGE_MS) {
      console.log(`[RECOVERY] Active job ${parsed.jobId} is older than 1 hour (${Math.round(age / 60000)}m), discarding.`);
      clearActiveJob();
      return null;
    }

    return parsed;
  } catch (e) {
    clearActiveJob();
    return null;
  }
};

/**
 * Xóa active job khỏi localStorage khi nhận kết quả hoặc gặp lỗi
 */
export const clearActiveJob = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.warn('[STORAGE] Could not clear active AI job', e);
  }
};

/**
 * Bước 3: Lắng nghe (SSE)
 * Dùng fetch gọi GET /api/v1/ai/job-listen/:jobId (kèm Token). Mở đường ống chờ kết quả.
 * Đọc luồng fetch stream, parse JSON từng event.
 */
export const listenAiHeavyJobStream = (
  jobId: string,
  onEvent: (event: AiJobStatusEvent) => void,
  onError: (error: any) => void
): (() => void) => {
  const abortController = new AbortController();
  let isAborted = false;

  (async () => {
    try {
      const token = getStoredToken();

      const headers: Record<string, string> = {
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const queryToken = token ? `?token=${encodeURIComponent(token)}` : '';
      const url = `/api/v1/ai/job-listen/${encodeURIComponent(jobId)}${queryToken}`;

      const response = await fetch(url, {
        method: 'GET',
        headers,
        signal: abortController.signal,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`SSE stream failed with status ${response.status}`);
      }

      if (!response.body) {
        throw new Error('ReadableStream not supported by browser environment.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (!isAborted) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (trimmed.startsWith('data:')) {
            const dataContent = trimmed.substring(5).trim();
            if (dataContent) {
              try {
                const parsed: AiJobStatusEvent = JSON.parse(dataContent);
                onEvent(parsed);

                if (parsed.status === 'completed' || parsed.status === 'failed') {
                  clearActiveJob();
                  return;
                }
              } catch (e) {
                console.error('[SSE JSON PARSE ERROR]', e, dataContent);
              }
            }
          }
        }
      }
    } catch (err: any) {
      if (!isAborted && err.name !== 'AbortError') {
        onError(err);
      }
    }
  })();

  return () => {
    isAborted = true;
    abortController.abort();
  };
};

/**
 * Chuẩn hóa và bóc tách danh sách câu hỏi từ kết quả SSE / Gemini
 */
export const normalizeGeneratedQuestions = (event: AiJobStatusEvent): any[] => {
  let list: any[] = [];
  
  if (Array.isArray(event.questions) && event.questions.length > 0) {
    list = event.questions;
  } else if (Array.isArray((event as any).data?.questions)) {
    list = (event as any).data.questions;
  } else if (typeof event.resultText === 'string' && event.resultText.trim()) {
    try {
      const rawText = event.resultText.trim();
      
      // Bước 2: Dùng Regex bóc tách chuỗi nằm trong ngoặc vuông [] để loại bỏ các câu chào hỏi thừa của AI
      const jsonMatch = rawText.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        if (Array.isArray(parsed)) {
          list = parsed;
        }
      } else {
        // Dự phòng trường hợp AI bọc trong Object { questions: [...] }
        const objMatch = rawText.match(/\{[\s\S]*\}/);
        if (objMatch) {
          const parsed = JSON.parse(objMatch[0]);
          if (Array.isArray(parsed.questions)) {
            list = parsed.questions;
          }
        }
      }
    } catch (e) {
      console.warn('[Question Normalizer] Lỗi bóc tách JSON từ AI resultText:', e);
    }
  }

  // Map sang định dạng GeneratedQuestionItem chuẩn theo đúng schema
  const normalized = list.map((item, idx) => {
    const qText = item.question || item.questionText || item.content || item.text || item.cauHoi || `Câu hỏi ${idx + 1}`;
    const diff = item.level || item.difficulty || item.mucDo || 'Thông hiểu';
    const explanation = item.explanation || item.giaiThich || item.loiGiai || '';
    const qMedia = item.questionMedia || item.mediaUrl || item.image || undefined;

    // Xác định index của đáp án đúng (từ 0 đến 3, hoặc ký tự 'A','B','C','D')
    let correctIdx = 0;
    if (typeof item.correctAnswer === 'number' && item.correctAnswer >= 0) {
      correctIdx = item.correctAnswer;
    } else if (typeof item.correctIndex === 'number' && item.correctIndex >= 0) {
      correctIdx = item.correctIndex;
    } else if (typeof item.correctAnswer === 'string') {
      const trimmedAns = item.correctAnswer.trim().toUpperCase();
      if (['A', 'B', 'C', 'D'].includes(trimmedAns)) {
        correctIdx = trimmedAns.charCodeAt(0) - 65;
      }
    }

    let options: Array<{ id: string; key: string; text: string; isCorrect: boolean }> = [];
    if (Array.isArray(item.options) && item.options.length > 0) {
      options = item.options.map((opt: any, optIdx: number) => {
        const key = (typeof opt === 'object' && opt.key) || String.fromCharCode(65 + optIdx);
        let text = typeof opt === 'string' ? opt : (opt.text || opt.content || opt.label || '');
        
        // Cần dọn dẹp (replace) tiền tố A, B, C, D trong text
        text = text.replace(/^[A-D][\.\:\)]\s*/i, '');
        
        let isCorrect = false;
        if (typeof opt === 'object' && opt.isCorrect !== undefined) {
          isCorrect = Boolean(opt.isCorrect);
        } else if (typeof opt === 'object' && opt.correct !== undefined) {
          isCorrect = Boolean(opt.correct);
        } else if (typeof item.correctAnswer === 'string' && typeof opt === 'string' && item.correctAnswer.trim().toLowerCase() === opt.trim().toLowerCase()) {
          isCorrect = true;
        } else {
          isCorrect = (optIdx === correctIdx);
        }

        return {
          id: `opt_${idx + 1}_${key}`,
          key,
          text,
          isCorrect
        };
      });
    } else {
      options = [
        { id: `opt_${idx + 1}_A`, key: 'A', text: 'Phương án A', isCorrect: correctIdx === 0 },
        { id: `opt_${idx + 1}_B`, key: 'B', text: 'Phương án B', isCorrect: correctIdx === 1 },
        { id: `opt_${idx + 1}_C`, key: 'C', text: 'Phương án C', isCorrect: correctIdx === 2 },
        { id: `opt_${idx + 1}_D`, key: 'D', text: 'Phương án D', isCorrect: correctIdx === 3 }
      ];
    }

    // Đảm bảo ít nhất một đáp án đúng
    if (!options.some(o => o.isCorrect) && options.length > 0) {
      options[0].isCorrect = true;
    }

    return {
      id: item.id || `ai_gen_${Date.now()}_${idx}`,
      questionText: qText,
      questionMedia: qMedia,
      options,
      explanation,
      difficulty: diff
    };
  });

  return normalized;
};

/**
 * PHẦN 5: Dropdown chọn Model
 * Gọi API GET /api/config/gemini-model để lấy danh sách model kích hoạt và model mặc định
 */
export const fetchGeminiModelsConfig = async (): Promise<{ models: string[]; defaultModel: string }> => {
  try {
    const res = await customFetch('/api/config/gemini-model', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      credentials: 'include'
    });

    if (res.ok) {
      const data = await res.json();
      const models = Array.isArray(data.models) ? data.models : ["gemini-2.5-flash", "gemini-1.5-flash", "gemini-1.5-pro", "gemini-2.0-flash"];
      const defaultModel = data.model || data.defaultModel || models[0] || "gemini-2.5-flash";
      return { models, defaultModel };
    }
  } catch (err) {
    console.warn('[CONFIG] Could not fetch gemini models, using defaults', err);
  }

  return {
    models: ["gemini-2.5-flash", "gemini-1.5-flash", "gemini-1.5-pro", "gemini-2.0-flash"],
    defaultModel: "gemini-2.5-flash"
  };
};
